import * as React from 'react'
import clsx from 'clsx'
import { ListContext, ListProps } from '../List'
import { useForkRef } from '../../hooks'
import { capitalize } from '../../utils'
import { SxProps, sx as sxFunc } from '../sx'
import styles from './index.module.scss'

export interface ListItemClasses {
  /** Styles applied to the (normally root) `component` element */
  root: string
  /** Styles applied to the inner `component` element if `divider={true}`. */
  divider: string
  /** State class applied to the root element if `selected={true}`. */
  selected: string
  /** Styles applied to the startIcon element if supplied. */
  startIcon: string
  /** Styles applied to the children element. */
  content: string
  /** Styles applied to the endIcon element if supplied. */
  endIcon: string
  /** Styles applied to the root element if `size="small"`. */
  sizeSmall: string
  /** Styles applied to the root element if `size="medium"`. */
  sizeMedium: string
  /** Styles applied to the root element if `size="large"`. */
  sizeLarge: string
}

export interface ListItemBaseProps
  extends React.HTMLAttributes<HTMLLIElement>,
    Pick<ListProps, 'size'> {
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<ListItemClasses>
  /**
   * If `true`, a 1px light border is added to the bottom of the list item.
   * @default false
   */
  divider?: boolean
  /**
   * Use to apply selected styling.
   * @default false
   */
  selected?: boolean
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The extra props for the slot components.
   * You can override the existing props or add new ones.
   *
   * This prop is an alias for the `componentsProps` prop, which will be deprecated in the future.
   *
   * @default {}
   */
  slotProps?: {
    root?: React.HTMLAttributes<HTMLDivElement>
  }
  /**
   * The components used for each slot inside.
   *
   * This prop is an alias for the `components` prop, which will be deprecated in the future.
   *
   * @default {}
   */
  slots?: {
    root?: React.ElementType
  }
  /**
   * Element placed before the children.
   */
  startIcon?: JSX.Element
  /**
   * Element placed after the children.
   */
  endIcon?: JSX.Element
}

export const ListItem = React.forwardRef<
  React.HTMLAttributes<HTMLLIElement>,
  ListItemBaseProps
>(function ListItem(props, ref) {
  const {
    children: childrenProp,
    classes,
    className,
    size = 'medium',
    divider = false,
    selected = false,
    slotProps = {},
    slots = {},
    sx,
    startIcon: startIconProp,
    endIcon: endIconProp,
    ...other
  } = props

  const context = React.useContext(ListContext)
  const childContext = React.useMemo(
    () => ({
      size: size !== 'medium' ? size : context.size,
    }),
    [context.size, size]
  )

  const listItemRef = React.useRef(null)

  const children = React.Children.toArray(childrenProp)

  const handleRef = useForkRef(listItemRef, ref)

  const Root = slots.root || 'div'
  const rootProps = slotProps.root || {}

  const startIcon = startIconProp && (
    <span
      className={clsx(
        styles.startIcon,
        classes?.startIcon,
        childContext.size && styles[childContext.size]
      )}
    >
      {startIconProp}
    </span>
  )

  const endIcon = endIconProp && (
    <span
      className={clsx(
        styles.endIcon,
        classes?.endIcon,
        childContext.size && styles[childContext.size]
      )}
    >
      {endIconProp}
    </span>
  )

  const content = children && (
    <span className={clsx(styles.content, classes?.content)}>{children}</span>
  )

  return (
    <ListContext.Provider value={childContext}>
      <Root
        {...rootProps}
        ref={handleRef}
        className={clsx(
          classes?.root,
          styles.root,
          childContext.size && [
            styles[childContext.size],
            //@ts-ignore
            classes?.[`size${capitalize(childContext.size)}`],
          ],
          divider && [classes?.divider, styles.divider],
          selected && [classes?.selected, styles.selected],
          className,
          sxFunc(sx)
        )}
        {...other}
      >
        {startIcon}
        {content}
        {endIcon}
      </Root>
    </ListContext.Provider>
  )
})
